'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useProductHeadingLevel } from '../product-heading-level';

export type ProductCardProps = {
  product: Product;
  inCarousel?: boolean;
  inForm?: boolean; // Added prop to indicate form context
  onSelect?: (name: string) => void; // Added callback for selection
  isSelected?: boolean; // Added to track selection state
  className?: {
    main?: string;
  };
};

function ProductCardInternal({
  product,
  inCarousel = false,
  inForm = false,
  onSelect,
  isSelected = false,
}: ProductCardProps) {
  const listingHeading = useProductHeadingLevel();
  const Heading = inCarousel || inForm ? 'h3' : listingHeading;
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const { t } = useTranslations();
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });

  const formatPrice = (price: number) => {
    const [integerPart, decimalPart] = price.toFixed(2).split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${formattedInteger}.${decimalPart}`;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (inForm && onSelect) {
      e.preventDefault();
      onSelect(product.name);
    }
  };

  const Component = inForm ? 'div' : Link;

  return (
    <Component
      {...(!inForm && {
        prefetch: false,
        href: productUrl,
      })}
      onClick={handleClick}
      {...(inForm && {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': isSelected,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect?.(product.name);
          }
        },
      })}
      className={cn(
        'relative col-span-1 flex w-full flex-col items-start justify-start gap-3 sm:gap-4',
        !inCarousel && 'p-0 lg:p-4',
        inForm && 'cursor-pointer p-4',
        inForm && isSelected ? 'border-2 border-primary' : 'border-2 border-transparent',
      )}
    >
      {!priceDisplay.priceOnRequest && priceDisplay.showPreviousPrice && (
        <div
          className={cn(
            'absolute z-[4] flex items-center justify-center !rounded-full bg-[#9B0A0A] text-white sm:right-2 sm:top-14 sm:h-14 sm:w-14 lg:right-6 lg:top-16 lg:h-16 lg:w-16 xl:top-20 xl:h-[78px] xl:w-[78px]',
            inCarousel ? 'right-0 top-12 h-12 w-12' : 'right-2 top-10 h-10 w-10',
          )}
        >
          <p
            className={cn(
              'font-medium sm:text-base lg:text-lg xl:text-2xl',
              inCarousel ? 'text-sm' : 'text-xs',
            )}
          >
            -{priceDisplay.discountLabel}%
          </p>
        </div>
      )}
      <Heading
        {...(Heading === 'h2' ? { 'data-ea-visual-tag': 'h3' } : {})}
        className={cn(
          'line-clamp-1 tracking-tighter text-black',
          inCarousel
            ? 'text-lg font-medium sm:text-xl lg:text-2xl'
            : 'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl',
        )}
      >
        {product.name}
      </Heading>
      <Image
        src={image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(image, product?.name)}
        width={0}
        height={0}
        className={cn('aspect-square w-full object-contain object-center p-4')}
        sizes="(max-width: 640px) 60vw, (max-width: 768px) 50vw, 30vw"
      />
      {priceDisplay.priceOnRequest ? (
        <strong className="text-primary">{t('text2278')}</strong>
      ) : (
        <div className="flex flex-col items-start gap-1">
          {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
            <span className="text-xs text-muted-foreground">
              {t('text1926')} {formatPrice(priceDisplay.mocPrice)} €
            </span>
          )}
          <div className="flex items-baseline justify-start gap-3 lg:gap-4">
            <strong
              className={cn(
                'font-semibold tracking-tighter text-primary',
                inCarousel ? 'text-2xl lg:text-3xl' : 'text-base sm:text-lg lg:text-xl xl:text-2xl',
              )}
            >
              {formatPrice(priceDisplay.current!)} €
            </strong>
            {priceDisplay.showPreviousPrice && priceDisplay.beforeDiscount != null && (
              <p className={cn('text-sm tracking-tighter line-through sm:text-base xl:text-lg')}>
                {formatPrice(priceDisplay.beforeDiscount)} €
              </p>
            )}
          </div>
          {priceDisplay.secondary != null && (
            <span className="text-xs text-muted-foreground">
              {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
              {formatPrice(priceDisplay.secondary)} €{' '}
              {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
            </span>
          )}
        </div>
      )}
    </Component>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider state={{ product: props.product }}>
      <ProductCardInternal {...props} />
    </VariantsProvider>
  );
}
